import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FIELD_SOURCES, fieldLabel, coerceSchema, newBlock, computeProblems,
  wordCount, CLAUSE_SUMMARY_MAX_WORDS, renderSubmissionHtml,
  FILL_FIELDS, fillFieldLabel, newInputToken, newBlockId,
  HEADING_WEIGHTS, TEXT_SIZES, safeHref,
} from '../lib/hostedForm';
import { sectionsOf } from '../lib/longDocument';
import {
  resolveEditableVersion, createDraftFrom, saveDraftContent, discardDraft,
  publishVersion, countSuppliersOnVersion,
} from '../lib/documentDrafts';
import PublishVersionDialog from '../components/documents/PublishVersionDialog';
import { useCurrentInternalUser } from '../lib/currentUser';

// Sample answers for "Preview as supplier" — realistic enough that a wrong
// prefill mapping or an out-of-place field shows up here, not in front of a
// real supplier (document-editor §6).
const sampleForInput = (ftype) => {
  // An attachment answer is a { url, name } object, not a string — the preview
  // has to hand renderSubmissionHtml the same SHAPE the real answer has, or it
  // demonstrates a rendering the supplier will never see.
  if (ftype === 'attachment') return { url: '#', name: 'environmental-policy-2026.pdf' };
  return {
    first_name: 'Sam', surname: 'Rivera', their_role: 'Operations Manager',
    email: 'sam.rivera@example.com', phone: '020 7946 0000', fill_date: '(today)',
    yes_no: 'Yes', number: '42', short_text: 'Sample answer',
    long_text: 'Sample longer answer across a line or two.',
  }[ftype] || 'Sample';
};

// Hosted-forms PROSE document editor (full page, not a modal). Authors the
// read-and-sign policy documents (NDA / code of conduct) as an ordered list of
// blocks: heading, paragraph (with inline identity-field chips), clause
// (summary + full wording, always tickable) and a single signature block.
//
// Editor = CONTENT. Saving a draft updates form_schema in place; publishing an
// already-live document creates a NEW version (supersedes the old row) so a
// content change can't silently alter a document suppliers already signed.
// Settings (reminders, enforcement) live in Configure and never version — this
// page never touches them.

const FONT = "font-['Metropolis']";
const escapeHtml = (s) => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// ---------------------------------------------------------------------------
// Paragraph editor — a contentEditable surface whose content serialises to the
// token model (text runs + inline field chips). Fields are inserted at the
// caret from the palette via the imperative `insertField`.
// ---------------------------------------------------------------------------
const ParagraphEditor = forwardRef(function ParagraphEditor({ lines, onChange, onFocus, placeholder }, ref) {
  const elRef = useRef(null);
  const focusedRef = useRef(false);
  // Remember the last caret position INSIDE the editor. A palette action (e.g.
  // the custom-field composer) can steal focus while the author types a label,
  // so we fall back to this saved range to drop the chip where the cursor was.
  const savedRange = useRef(null);
  const rememberSelection = useCallback(() => {
    const el = elRef.current;
    const sel = window.getSelection();
    if (el && sel && sel.rangeCount && el.contains(sel.anchorNode)) savedRange.current = sel.getRangeAt(0).cloneRange();
  }, []);
  const activeRange = useCallback(() => {
    const el = elRef.current;
    const sel = window.getSelection();
    if (el && sel && sel.rangeCount && el.contains(sel.anchorNode)) return sel.getRangeAt(0);
    if (el && savedRange.current && el.contains(savedRange.current.startContainer)) return savedRange.current;
    return null;
  }, []);

  // Render ONE line's inline tokens (text runs w/ bold/italic/underline/size,
  // links, and field/input chips).
  const inlineToHtml = useCallback((toks) => (toks || []).map(t => {
    if (t.t === 'field') return `<span class="hf-chip" contenteditable="false" data-source="${t.source}">${escapeHtml(fieldLabel(t.source))}</span>`;
    if (t.t === 'input') return `<span class="hf-chip hf-chip-input" contenteditable="false" data-input-id="${escapeHtml(t.id)}" data-ftype="${escapeHtml(t.ftype)}" data-label="${escapeHtml(t.label)}" data-optional="${t.optional ? '1' : ''}">${escapeHtml(t.label)}${t.optional ? ' (optional)' : ''}</span>`;
    if (t.t === 'link') return `<a href="${escapeHtml(t.href)}" class="hf-link">${escapeHtml(t.v)}</a>`;
    let h = escapeHtml(t.v);
    if (t.i) h = `<em>${h}</em>`;
    if (t.b) h = `<strong>${h}</strong>`;
    if (t.u) h = `<u>${h}</u>`;
    // Size is a STEP, carried as a class the serialiser reads back. Never an
    // inline font-size — a number here would survive into the document and
    // override the stylesheet, which is the thing the brief rules out.
    if (t.size && t.size !== 'base') h = `<span class="hf-sz-${escapeHtml(t.size)}">${h}</span>`;
    return h;
  }).join(''), []);

  // Render the full lines model. Plain lines are <div>s (so paragraph breaks
  // survive); runs of list lines group into a <ul>/<ol>. A run breaks when the
  // marker kind OR the indent level changes, and indent is emitted as nesting
  // so the browser draws real nested lists rather than padded flat ones.
  const linesToHtml = useCallback((lns) => {
    let out = '';
    let buf = [];
    let bufKind = null;
    let bufIndent = 0;
    const flushBuf = () => {
      if (!buf.length) return;
      const tag = bufKind === 'numbered' ? 'ol' : 'ul';
      // Nest by wrapping: one extra list per indent level.
      let html = `<${tag}>${buf.join('')}</${tag}>`;
      for (let d = 0; d < bufIndent; d += 1) html = `<${tag}><li>${html}</li></${tag}>`;
      out += html;
      buf = [];
    };
    (lns || []).forEach(l => {
      const h = inlineToHtml(l.tokens) || '<br>';
      const kind = l.bullet ? 'bullet' : l.numbered ? 'numbered' : null;
      const indent = Math.max(0, Math.min(3, Number(l.indent) || 0));
      if (kind) {
        if (kind !== bufKind || indent !== bufIndent) { flushBuf(); bufKind = kind; bufIndent = indent; }
        buf.push(`<li>${h}</li>`);
      } else {
        flushBuf(); bufKind = null; bufIndent = 0;
        out += indent ? `<div class="hf-ind-${indent}">${h}</div>` : `<div>${h}</div>`;
      }
    });
    flushBuf();
    return out || '<div><br></div>';
  }, [inlineToHtml]);

  // Flatten the contentEditable DOM into the lines model. Block boundaries —
  // <div>/<p>/<li> and <br> — end a line; <li> (or an .hf-bullet div) makes a
  // bullet line. Nested/pasted structure flattens cleanly, so pasting a
  // multi-paragraph document KEEPS its paragraph breaks instead of collapsing
  // into one run. Bold/italic tracked by tag, inline style AND computed style
  // (pasted formatting is often class-based, which the first two miss).
  // Set when `lines` changed because WE just serialised the live DOM — the DOM
  // is already current, so the sync effect below must NOT rebuild innerHTML
  // (that detaches savedRange, which is why a custom field inserted after the
  // composer input stole focus landed at the top instead of the caret).
  const skipNextSync = useRef(false);
  const serialize = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const out = [];
    let cur = [];
    // `kind` is 'bullet' | 'numbered' | null; `indent` is the nesting depth.
    const flush = (kind, indent) => {
      const merged = [];
      for (const t of cur) {
        const prev = merged[merged.length - 1];
        // Runs only merge when EVERY mark matches — size included, or a large
        // run would silently absorb the normal text next to it.
        if (t.t === 'text' && prev && prev.t === 'text'
          && !!prev.b === !!t.b && !!prev.i === !!t.i && !!prev.u === !!t.u && prev.size === t.size) prev.v += t.v;
        else merged.push({ ...t });
      }
      if (!merged.length || merged[0].t !== 'text') merged.unshift({ t: 'text', v: '' });
      if (merged[merged.length - 1].t !== 'text') merged.push({ t: 'text', v: '' });
      out.push({
        bullet: kind === 'bullet',
        ...(kind === 'numbered' ? { numbered: true } : {}),
        ...(indent ? { indent: Math.min(3, indent) } : {}),
        tokens: merged,
      });
      cur = [];
    };
    const walk = (node, ctx, kind, indent) => {
      node.childNodes.forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          if (child.nodeValue) cur.push({ t: 'text', v: child.nodeValue, ...(ctx.b ? { b: true } : {}), ...(ctx.i ? { i: true } : {}), ...(ctx.u ? { u: true } : {}), ...(ctx.size ? { size: ctx.size } : {}) });
          return;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) return;
        const src = child.getAttribute && child.getAttribute('data-source');
        if (src) { cur.push({ t: 'field', source: src }); return; }
        const inputId = child.getAttribute && child.getAttribute('data-input-id');
        if (inputId) {
          cur.push({
            t: 'input', id: inputId,
            ftype: child.getAttribute('data-ftype') || 'short_text',
            label: child.getAttribute('data-label') || fillFieldLabel(child.getAttribute('data-ftype')),
            optional: child.getAttribute('data-optional') === '1',
          });
          return;
        }
        const tag = child.tagName;
        // A link is a LEAF: its text belongs to the link, not to the run
        // around it. An unsafe href degrades to plain text rather than
        // silently keeping a javascript: URL in the stored document.
        if (tag === 'A') {
          const href = safeHref(child.getAttribute('href'));
          const text = child.textContent || '';
          if (href) cur.push({ t: 'link', v: text, href });
          else if (text) cur.push({ t: 'text', v: text, ...(ctx.b ? { b: true } : {}), ...(ctx.i ? { i: true } : {}), ...(ctx.u ? { u: true } : {}) });
          return;
        }
        if (tag === 'BR') { flush(kind, indent); return; }
        if (tag === 'DIV' || tag === 'P' || tag === 'LI') {
          if (cur.length) flush(kind, indent);
          const isLi = tag === 'LI';
          // A plain line carries its indent as a class; a list item inherits
          // the depth of the list it sits in.
          let lineIndent = indent;
          if (!isLi && child.classList) {
            for (let d = 1; d <= 3; d += 1) if (child.classList.contains(`hf-ind-${d}`)) lineIndent = d;
          }
          const k2 = isLi ? kind : (child.classList && child.classList.contains('hf-bullet') ? 'bullet' : null);
          walk(child, ctx, k2, lineIndent);
          if (cur.length) flush(k2, lineIndent);
          else if (isLi) flush(k2, lineIndent); // keep an empty list line
          return;
        }
        if (tag === 'UL' || tag === 'OL') {
          // Nesting IS the indent. Entering a list from inside a list is one
          // level deeper — which is what execCommand('indent') produces.
          const k2 = tag === 'OL' ? 'numbered' : 'bullet';
          walk(child, ctx, k2, kind ? Math.min(3, indent + 1) : indent);
          return;
        }
        // Chrome's execCommand('indent') wraps a plain line in a blockquote.
        if (tag === 'BLOCKQUOTE') { walk(child, ctx, kind, Math.min(3, indent + 1)); return; }
        const style = (child.getAttribute && child.getAttribute('style')) || '';
        let b = ctx.b || tag === 'B' || tag === 'STRONG' || /font-weight\s*:\s*(bold|[6-9]00)/i.test(style);
        let i = ctx.i || tag === 'I' || tag === 'EM' || /font-style\s*:\s*italic/i.test(style);
        let u = ctx.u || tag === 'U' || /text-decoration[^;]*underline/i.test(style);
        // Size comes ONLY from our own class. A pasted font-size is ignored on
        // purpose — that's the Word formatting the brief rules out, and it
        // would override the document stylesheet.
        let size = ctx.size;
        if (child.classList) {
          if (child.classList.contains('hf-sz-sm')) size = 'sm';
          else if (child.classList.contains('hf-sz-lg')) size = 'lg';
        }
        if (!b || !i || !u) {
          try {
            const cs = window.getComputedStyle(child);
            if (!b && (cs.fontWeight === 'bold' || parseInt(cs.fontWeight, 10) >= 600)) b = true;
            if (!i && cs.fontStyle === 'italic') i = true;
            if (!u && /underline/i.test(cs.textDecorationLine || cs.textDecoration || '')) u = true;
          } catch { /* not laid out — tag/style checks stand */ }
        }
        walk(child, { b, i, u, size }, kind, indent);
      });
    };
    walk(el, { b: false, i: false }, null, 0);
    if (cur.length) flush(null, 0);
    if (!out.length) out.push({ bullet: false, tokens: [{ t: 'text', v: '' }] });
    skipNextSync.current = true;
    onChange(out);
  }, [onChange]);

  // Populate the DOM from the lines on mount + external change, never while
  // focused (that would fight the caret).
  useEffect(() => {
    if (!elRef.current || focusedRef.current) return;
    // Skip the rebuild when this change is our own serialize echoing back —
    // the DOM already matches, and rebuilding would detach the saved caret.
    if (skipNextSync.current) { skipNextSync.current = false; return; }
    elRef.current.innerHTML = linesToHtml(lines);
  }, [lines, linesToHtml]);

  useImperativeHandle(ref, () => ({
    // Bold / italic on the current selection (execCommand is deprecated but
    // still the pragmatic way to toggle inline marks in a contentEditable;
    // styleWithCSS=false keeps it to <b>/<i> tags the serialiser understands).
    exec(cmd) {
      const el = elRef.current;
      if (!el) return;
      el.focus();
      try { document.execCommand('styleWithCSS', false, false); } catch { /* older browsers */ }
      document.execCommand(cmd);
      serialize();
    },
    // Toggle a bullet list on the current line(s). The browser builds the
    // <ul>/<li> DOM; serialize reads <li> back as bullet lines.
    toggleBullet() {
      const el = elRef.current;
      if (!el) return;
      el.focus();
      try { document.execCommand('insertUnorderedList'); } catch { /* unsupported */ }
      serialize();
    },
    toggleNumbered() {
      const el = elRef.current;
      if (!el) return;
      el.focus();
      try { document.execCommand('insertOrderedList'); } catch { /* unsupported */ }
      serialize();
    },
    // Indent / outdent — the "tabbing" ask. Inside a list the browser nests
    // the list, which serialize reads as a deeper indent; on a plain line it
    // produces a blockquote, which serialize also reads as indent. Either way
    // the MODEL carries a level, so the stored document never depends on the
    // browser's choice of wrapper.
    indent(dir) {
      const el = elRef.current;
      if (!el) return;
      el.focus();
      try { document.execCommand(dir > 0 ? 'indent' : 'outdent'); } catch { /* unsupported */ }
      serialize();
    },
    // Wrap the selection in a size STEP. Removing the step unwraps rather than
    // setting "normal", so the text goes back to inheriting the stylesheet.
    setSize(size) {
      const el = elRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || sel.isCollapsed || !el.contains(sel.anchorNode)) return;
      const range = sel.getRangeAt(0);
      const frag = range.extractContents();
      if (size && size !== 'base') {
        const span = document.createElement('span');
        span.className = `hf-sz-${size}`;
        span.appendChild(frag);
        range.insertNode(span);
      } else {
        // Strip any size wrappers inside the selection, then reinsert.
        const host = document.createElement('div');
        host.appendChild(frag);
        host.querySelectorAll('.hf-sz-sm, .hf-sz-lg').forEach(n => {
          while (n.firstChild) n.parentNode.insertBefore(n.firstChild, n);
          n.remove();
        });
        while (host.firstChild) range.insertNode(host.lastChild);
      }
      sel.removeAllRanges();
      serialize();
    },
    // Turn the selection into a link. Refuses anything that isn't http(s) or
    // mailto — the signed document is served from storage, so a javascript:
    // href here would execute in that origin.
    insertLink(href) {
      const el = elRef.current;
      const safe = safeHref(href);
      if (!el || !safe) return false;
      el.focus();
      const range = activeRange();
      if (!range || range.collapsed) return false;
      const a = document.createElement('a');
      a.className = 'hf-link';
      a.setAttribute('href', safe);
      a.appendChild(range.extractContents());
      range.insertNode(a);
      serialize();
      return true;
    },
    insertField(source) {
      const el = elRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      const chip = document.createElement('span');
      chip.className = 'hf-chip';
      chip.setAttribute('contenteditable', 'false');
      chip.setAttribute('data-source', source);
      chip.textContent = fieldLabel(source);
      const spacer = document.createTextNode(' ');
      if (sel && sel.rangeCount && el.contains(sel.anchorNode)) {
        const range = sel.getRangeAt(0);
        range.deleteContents();
        range.insertNode(spacer);
        range.insertNode(chip);
        range.setStartAfter(spacer);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      } else {
        el.appendChild(chip);
        el.appendChild(spacer);
      }
      serialize();
    },
    // Supplier-fills input chip — same insertion mechanics, a distinct chip that
    // carries the token's id/type/label/optional so serialize can rebuild it.
    insertInput(token) {
      const el = elRef.current;
      if (!el) return;
      const chip = document.createElement('span');
      chip.className = 'hf-chip hf-chip-input';
      chip.setAttribute('contenteditable', 'false');
      chip.setAttribute('data-input-id', token.id);
      chip.setAttribute('data-ftype', token.ftype);
      chip.setAttribute('data-label', token.label);
      chip.setAttribute('data-optional', token.optional ? '1' : '');
      chip.textContent = token.label + (token.optional ? ' (optional)' : '');
      const spacer = document.createTextNode(' ');
      const range = activeRange();
      if (range) {
        range.deleteContents();
        range.insertNode(spacer);
        range.insertNode(chip);
        range.setStartAfter(spacer);
        range.collapse(true);
        el.focus();
        const s = window.getSelection();
        s.removeAllRanges();
        s.addRange(range);
      } else {
        el.appendChild(chip);
        el.appendChild(spacer);
        el.focus();
      }
      serialize();
    },
  }), [serialize, activeRange]);

  const isEmpty = !lines || (lines.length === 1 && !lines[0].bullet
    && (lines[0].tokens || []).length === 1 && lines[0].tokens[0].t === 'text' && !lines[0].tokens[0].v);

  return (
    <div className="relative">
      {isEmpty && !focusedRef.current && (
        <span className={`${FONT} pointer-events-none absolute left-0 top-0 text-[15px] text-[#9BA08C] leading-relaxed`}>{placeholder}</span>
      )}
      <div
        ref={elRef}
        contentEditable
        suppressContentEditableWarning
        // Enter is ALLOWED (it starts a new line); defaultParagraphSeparator
        // keeps the browser on <div> lines the serialiser understands.
        onFocus={() => { focusedRef.current = true; try { document.execCommand('defaultParagraphSeparator', false, 'div'); } catch { /* */ } onFocus && onFocus(); }}
        onBlur={() => { focusedRef.current = false; rememberSelection(); serialize(); }}
        onInput={serialize}
        onKeyUp={rememberSelection}
        onMouseUp={rememberSelection}
        // hf-para carries the list/indent styling from the <style> block above
        // — ordered lists, nested lists and blockquote indents all need to look
        // here the way they will in the finished document.
        className={`${FONT} hf-para outline-none text-[15px] text-ink leading-relaxed min-h-[1.6em] focus:bg-[#92D400]/[0.03] rounded [&_li]:my-0.5`}
      />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Block chrome — move/delete controls shared by every block type.
// ---------------------------------------------------------------------------
function BlockShell({ label, first, last, onUp, onDown, onDelete, children, problem }) {
  return (
    <div className={`group relative rounded-xl border px-4 py-3 mb-3 bg-white transition-colors ${problem ? 'border-[#E0290A]/50' : 'border-line hover:border-[#DADCCD]'}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C]`}>{label}</span>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button type="button" onClick={onUp} disabled={first} title="Move up" className="w-6 h-6 rounded flex items-center justify-center text-[#51534A] hover:bg-[#EEF1E6] disabled:opacity-30 disabled:cursor-not-allowed">↑</button>
          <button type="button" onClick={onDown} disabled={last} title="Move down" className="w-6 h-6 rounded flex items-center justify-center text-[#51534A] hover:bg-[#EEF1E6] disabled:opacity-30 disabled:cursor-not-allowed">↓</button>
          <button type="button" onClick={onDelete} title="Delete block" className="w-6 h-6 rounded flex items-center justify-center text-[#51534A] hover:text-[#E0290A] hover:bg-[#E0290A]/10">✕</button>
        </div>
      </div>
      {children}
    </div>
  );
}

const inputCls = `${FONT} w-full bg-white border border-line rounded-lg text-ink px-3 py-2 text-sm focus:outline-none focus:border-[#92D400] transition-colors`;

export default function HostedFormEditor() {
  const { docId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  // The LIVE published version, when there is one. `doc` is what's being
  // edited — which is either a draft of `live`, or `live` itself before the
  // first edit is saved (the draft row is created lazily, so opening a
  // published document to read it doesn't leave a stray draft behind).
  const [live, setLive] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // 'draft' | 'publish' | 'discard'
  const [savedAt, setSavedAt] = useState(null);
  const [focusedBlockId, setFocusedBlockId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState('supplier'); // 'supplier' | 'document'
  const [publishOpen, setPublishOpen] = useState(false);
  const [affectedCount, setAffectedCount] = useState(0);
  const [countLoading, setCountLoading] = useState(false);
  const paraRefs = useRef({});
  const { user: internalUser } = useCurrentInternalUser();

  // Editing a published document means editing a DRAFT of it (§3a). True once
  // that draft row exists; false while `doc` is still the live row itself.
  const editingDraftOfLive = !!live && !!doc && doc.id !== live.id;

  // "Preview as supplier" — the current draft rendered through the SAME
  // renderer the signed copy uses, with sample identity + fill answers, so
  // prefill mappings resolve exactly as a supplier would see them (§6).
  const previewHtml = useMemo(() => {
    if (!schema) return '';
    const s = coerceSchema(schema);
    const fields = {};
    FIELD_SOURCES.forEach(src => { fields[src.key] = src.example; });
    const ticks = {};
    s.blocks.forEach(b => {
      if (b.type === 'clause') ticks[b.id] = true;
      if (b.type === 'paragraph') (b.lines || []).forEach(ln => (ln.tokens || []).forEach(t => {
        if (t.t === 'input') fields[t.id] = sampleForInput(t.ftype);
      }));
    });
    return renderSubmissionHtml(s, {
      fields, ticks, signerName: 'Sam Rivera', signedAtLabel: '(today, on submit)',
      docLabel: doc?.document_name || 'Document', version: doc?.version_value || null,
    });
  }, [schema, doc?.document_name, doc?.version_value]);

  // "View document" — the same branded renderer, but BLANK (no sample answers,
  // no signer). Shows the document as it reads: cover, branding and content.
  const documentHtml = useMemo(() => {
    if (!schema) return '';
    return renderSubmissionHtml(coerceSchema(schema), {
      fields: {}, ticks: {}, signerName: '', signedAtLabel: '',
      docLabel: doc?.document_name || 'Document', version: doc?.version_value || null,
      subtitle: doc?.internal_reference || '',
    });
  }, [schema, doc?.document_name, doc?.version_value, doc?.internal_reference]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      // Resolves what this URL should actually edit: an existing draft of a
      // live document if one is in progress, otherwise the row itself.
      const { row, live: liveRow, error: err } = await resolveEditableVersion(docId);
      if (!alive) return;
      if (err || !row) { setError(err || 'Document not found.'); setLoading(false); return; }
      setDoc(row);
      setLive(liveRow || null);
      setSchema(coerceSchema(row.form_schema));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [docId]);

  const problems = useMemo(() => (schema ? computeProblems(schema) : []), [schema]);
  const problemsByBlock = useMemo(() => {
    const m = {};
    for (const p of problems) if (p.blockId) (m[p.blockId] = m[p.blockId] || []).push(p);
    return m;
  }, [problems]);

  const setBlocks = useCallback((updater) => {
    setSchema(prev => ({ ...prev, blocks: typeof updater === 'function' ? updater(prev.blocks) : updater }));
    setSavedAt(null);
  }, []);

  const updateBlock = useCallback((id, patch) => {
    setBlocks(blocks => blocks.map(b => b.id === id ? { ...b, ...patch } : b));
  }, [setBlocks]);

  const deleteBlock = useCallback((id) => {
    setBlocks(blocks => blocks.filter(b => b.id !== id));
  }, [setBlocks]);

  const moveBlock = useCallback((id, dir) => {
    setBlocks(blocks => {
      const i = blocks.findIndex(b => b.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= blocks.length) return blocks;
      const next = blocks.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }, [setBlocks]);

  const addBlock = useCallback((type) => {
    setBlocks(blocks => {
      const block = newBlock(type);
      // A signature always goes last; everything else after the focused block
      // (or at the end if nothing's focused).
      if (type === 'signature') return [...blocks, block];
      const idx = focusedBlockId ? blocks.findIndex(b => b.id === focusedBlockId) : -1;
      if (idx < 0) return [...blocks, block];
      const next = blocks.slice();
      next.splice(idx + 1, 0, block);
      return next;
    });
  }, [setBlocks, focusedBlockId]);

  const insertFieldIntoFocused = useCallback((source) => {
    const handle = focusedBlockId && paraRefs.current[focusedBlockId];
    if (!handle) { setError('Click into a paragraph first, then add a field.'); setTimeout(() => setError(null), 3500); return; }
    handle.insertField(source);
  }, [focusedBlockId]);

  const insertInputIntoFocused = useCallback((ftype) => {
    const handle = focusedBlockId && paraRefs.current[focusedBlockId];
    if (!handle) { setError('Click into a paragraph first, then add a field.'); setTimeout(() => setError(null), 3500); return; }
    handle.insertInput(newInputToken(ftype));
  }, [focusedBlockId]);

  // Custom fill-in field — the author names it and picks a type. Drops in at the
  // paragraph's remembered caret (the composer input steals focus, so the editor
  // falls back to its saved range). Empty label → no-op.
  const [customFieldLabel, setCustomFieldLabel] = useState('');
  const [customFieldType, setCustomFieldType] = useState('short_text');
  const insertCustomFieldIntoFocused = useCallback(() => {
    const handle = focusedBlockId && paraRefs.current[focusedBlockId];
    if (!handle) { setError('Click into a paragraph first, then add a field.'); setTimeout(() => setError(null), 3500); return; }
    const label = customFieldLabel.trim();
    if (!label) { setError('Give the field a name first.'); setTimeout(() => setError(null), 3000); return; }
    const tok = newInputToken(customFieldType);
    tok.label = label;
    handle.insertInput(tok);
    setCustomFieldLabel('');
  }, [focusedBlockId, customFieldLabel, customFieldType]);

  const hasSignature = schema?.blocks?.some(b => b.type === 'signature');

  // The sections the supplier will actually see, derived the same way the
  // reader derives them — so the editor can't number a section differently
  // from the contents list it produces.
  const editorSections = useMemo(() => sectionsOf(schema), [schema]);

  // Save the working content. On a published document this MINTS THE DRAFT the
  // edit belongs in (§3a) rather than writing over live content — which the DB
  // now refuses outright (fusion_documents_published_content_immutable).
  const saveDraft = useCallback(async () => {
    if (busy || !doc) return;
    setBusy('draft'); setError(null);
    try {
      if (doc.status === 'active') {
        const draft = await createDraftFrom(doc, { form_schema: schema });
        setDoc(draft);
        setLive(doc);
      } else {
        await saveDraftContent(doc, { form_schema: schema });
      }
      setSavedAt(new Date());
    } catch (e) {
      setError(e.message || 'Could not save the draft.');
    }
    setBusy(null);
  }, [busy, doc, schema]);

  // Throw away an in-progress draft and go back to reading the live version.
  const discardWorkingDraft = useCallback(async () => {
    if (busy || !editingDraftOfLive) return;
    setBusy('discard'); setError(null);
    try {
      await discardDraft(doc.id);
      setDoc(live);
      setSchema(coerceSchema(live.form_schema));
      setSavedAt(null);
    } catch (e) {
      setError(e.message || 'Could not discard the draft.');
    }
    setBusy(null);
  }, [busy, editingDraftOfLive, doc, live]);

  const runPublish = useCallback(async (choice) => {
    if (!doc) return;
    setBusy('publish'); setError(null);
    try {
      // The edit may never have been saved — mint the draft now so the publish
      // has a row of its own to promote and the live version is never rewritten.
      let draft = doc;
      if (live && doc.id === live.id) draft = await createDraftFrom(live, { form_schema: schema });
      const result = await publishVersion({
        draft,
        live,
        content: { form_schema: schema },
        publishedByUserId: internalUser?.id || null,
        choice,
      });
      // The version IS published at this point. If some of the "please sign
      // again" emails didn't get out, say so here rather than navigating away
      // and leaving the reviewer believing everyone was told.
      if (result?.notified?.failed) {
        setError(`Published — but ${result.notified.failed} supplier ${result.notified.failed === 1 ? 'email' : 'emails'} couldn't be sent (no contact address, or the mail service refused). Chase them from the document's version history.`);
        setBusy(null);
        setPublishOpen(false);
        return;
      }
      navigate('/documents');
    } catch (e) {
      setError(e.message || 'Could not publish.');
      setBusy(null);
      setPublishOpen(false);
    }
  }, [doc, live, schema, internalUser, navigate]);

  // Publishing over a version suppliers may have signed is a decision about
  // THEM (§1), so it goes through the choice dialog — which needs the count of
  // who actually signed the live version. A first publish has no previous
  // version and nobody to re-sign, so it just goes.
  const startPublish = useCallback(async () => {
    if (busy || !doc || problems.length > 0) return;
    if (!live) { await runPublish(null); return; }
    setPublishOpen(true);
    setCountLoading(true);
    const n = await countSuppliersOnVersion(live);
    setAffectedCount(n);
    setCountLoading(false);
  }, [busy, doc, live, problems.length, runPublish]);

  if (loading) {
    return <div className={`${FONT} min-h-screen bg-canvas flex items-center justify-center text-[#51534A] tracking-[3px] uppercase text-sm`}>Loading…</div>;
  }
  if (error && !doc) {
    return (
      <div className={`${FONT} min-h-screen bg-canvas flex flex-col items-center justify-center gap-4`}>
        <p className="text-[#E0290A] text-sm">{error}</p>
        <button onClick={() => navigate('/documents')} className="text-[#2E8FA1] text-sm hover:underline">← Back to Fusion Documents</button>
      </div>
    );
  }

  const publishLabel = busy === 'publish' ? 'Publishing…'
    : problems.length > 0 ? `Publish — ${problems.length} problem${problems.length === 1 ? '' : 's'}`
    : live ? `Publish v${(Number(live.version_value) || 1) + 1}` : 'Publish';

  // What the pill says depends on which row is open: the live one, or a draft
  // of it. Never just "Draft" when a published version is still out there.
  const statusPill = live
    ? (editingDraftOfLive
        ? { label: `Draft of v${doc.version_value || (Number(live.version_value) || 1) + 1}`, live: false }
        : { label: `Published · v${live.version_value || 1}`, live: true })
    : { label: 'Draft', live: false };

  return (
    <div className={`${FONT} min-h-screen bg-canvas`}>
      {/* Authoring-surface styles. The size steps and indents mirror what the
          document stylesheet in renderSubmissionHtml resolves them to, so what
          the author sees here is what the supplier gets. Change both together
          if the scale ever moves. */}
      <style>{`
        .hf-chip{display:inline-flex;align-items:center;background:#2E8FA1;color:#fff;border-radius:9999px;padding:1px 8px;margin:0 1px;font-size:12px;font-weight:700;white-space:nowrap;user-select:none;}
        .hf-chip-input{background:#2F6E14;}
        .hf-sz-sm{font-size:.86em;}
        .hf-sz-lg{font-size:1.18em;}
        .hf-ind-1{margin-left:26px;}
        .hf-ind-2{margin-left:52px;}
        .hf-ind-3{margin-left:78px;}
        .hf-link{color:#2F6E14;text-decoration:underline;}
        .hf-para ol{list-style:decimal;padding-left:22px;margin:0 0 8px;}
        .hf-para ul{list-style:disc;padding-left:22px;margin:0 0 8px;}
        .hf-para blockquote{margin:0 0 0 26px;border:0;padding:0;}
      `}</style>

      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-line">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <button onClick={() => navigate('/documents')} className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer">← Fusion Documents</button>
            <div className="flex items-center gap-2.5 mt-0.5">
              <h1 className="text-lg font-extrabold text-ink truncate">{doc.document_name || 'Untitled form'}</h1>
              <span className={`text-[9px] font-bold uppercase tracking-[0.15em] rounded-full px-2 py-0.5 ${statusPill.live ? 'bg-[#92D400]/15 text-fusion-forest border border-[#92D400]/40' : 'bg-[#9BA08C]/15 text-[#51534A] border border-[#9BA08C]/40'}`}>
                {statusPill.label}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {savedAt && <span className="text-[11px] text-[#51534A]">Draft saved</span>}
            <button type="button" onClick={() => { setPreviewMode('document'); setPreviewOpen(true); }}
              className="bg-white border border-line rounded-xl text-fusion-mid-grey text-xs font-semibold px-4 py-2 hover:border-[#DADCCD] transition-colors">
              View document
            </button>
            <button type="button" onClick={() => { setPreviewMode('supplier'); setPreviewOpen(true); }}
              className="bg-white border border-[#2E8FA1]/40 rounded-xl text-[#2E8FA1] text-xs font-semibold px-4 py-2 hover:border-[#2E8FA1] transition-colors">
              Preview as supplier
            </button>
            <button type="button" onClick={saveDraft} disabled={!!busy}
              className="bg-white border border-line rounded-xl text-fusion-mid-grey text-xs font-semibold px-4 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
              {busy === 'draft' ? 'Saving…' : 'Save draft'}
            </button>
            <button type="button" onClick={startPublish} disabled={!!busy || problems.length > 0}
              title={problems.length > 0 ? 'Fix the problems on the right before publishing' : undefined}
              className="bg-[#92D400] text-[#131313] rounded-xl text-xs font-bold px-5 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              {publishLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Editing a published document edits a DRAFT of it — say so, because the
          version suppliers are signing hasn't changed and won't until publish. */}
      {live && (
        <div className="max-w-6xl mx-auto px-6 pt-4">
          <div className="rounded-xl border-[1.5px] border-[#E89A2E]/40 bg-[#E89A2E]/[0.07] px-4 py-3 flex items-start justify-between gap-4">
            <p className="text-[12px] text-[#7A4E10] leading-snug">
              <strong className="font-extrabold">
                {editingDraftOfLive ? `You're editing a draft of v${doc.version_value}.` : `v${live.version_value || 1} is live.`}
              </strong>{' '}
              Suppliers keep reading and signing v{live.version_value || 1} until you publish
              {editingDraftOfLive ? '' : ' — your first save starts a draft'}. Signed copies of it never change.
            </p>
            {editingDraftOfLive && (
              <button
                type="button"
                onClick={discardWorkingDraft}
                disabled={!!busy}
                className="flex-shrink-0 bg-white border border-line rounded-lg text-[11px] font-bold text-[#51534A] px-3 py-1.5 hover:border-[#DADCCD] disabled:opacity-50">
                {busy === 'discard' ? 'Discarding…' : 'Discard draft'}
              </button>
            )}
          </div>
        </div>
      )}

      <PublishVersionDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        onConfirm={(choice) => runPublish(choice)}
        documentName={doc.document_name || 'Untitled form'}
        liveVersion={live?.version_value || 1}
        nextVersion={editingDraftOfLive ? doc.version_value : (Number(live?.version_value) || 1) + 1}
        affectedCount={affectedCount}
        countLoading={countLoading}
        busy={busy === 'publish'}
        error={error}
      />

      <div className="max-w-6xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
        {/* Canvas */}
        <div>
          {error && <p className="text-sm text-[#E0290A] mb-3">{error}</p>}
          {schema.blocks.map((b, i) => {
            const shell = {
              first: i === 0, last: i === schema.blocks.length - 1,
              onUp: () => moveBlock(b.id, -1), onDown: () => moveBlock(b.id, +1),
              onDelete: () => deleteBlock(b.id), problem: !!problemsByBlock[b.id],
            };
            if (b.type === 'heading') {
              const isH3 = b.level === 3;
              return (
                <BlockShell key={b.id} label={isH3 ? 'Sub-heading' : 'Heading'} {...shell}>
                  {/* Level and weight. Rob's call (2026-08-13): authors DO get
                      a weight choice on headings — Extra Light through Bold.
                      They still don't get a font family or a colour, so a
                      document can't drift away from the house style. */}
                  <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                    {[[2, 'H2'], [3, 'H3']].map(([lvl, glyph]) => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => updateBlock(b.id, { level: lvl })}
                        aria-pressed={(b.level || 2) === lvl}
                        title={lvl === 2 ? 'Heading' : 'Sub-heading'}
                        className={`${FONT} px-2 h-6 rounded text-[11px] font-bold ${(b.level || 2) === lvl ? 'bg-fusion-forest text-white' : 'text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink'}`}>
                        {glyph}
                      </button>
                    ))}
                    <span className="w-px h-4 bg-[#DADCCD] mx-1" aria-hidden="true" />
                    {HEADING_WEIGHTS.map(w => (
                      <button
                        key={w.key}
                        type="button"
                        onClick={() => updateBlock(b.id, { weight: w.key })}
                        aria-pressed={(b.weight || 'semibold') === w.key}
                        className={`${FONT} px-2 h-6 rounded text-[11px] ${(b.weight || 'semibold') === w.key ? 'bg-fusion-forest text-white' : 'text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink'}`}
                        style={{ fontWeight: w.key === 'light' ? 200 : w.key === 'bold' ? 700 : 600 }}>
                        {w.label}
                      </button>
                    ))}
                  </div>
                  <input
                    value={b.text}
                    onChange={e => updateBlock(b.id, { text: e.target.value })}
                    onFocus={() => setFocusedBlockId(b.id)}
                    placeholder={isH3 ? 'Sub-heading' : 'Section heading'}
                    className={`${FONT} w-full bg-transparent border-0 outline-none text-ink placeholder:text-[#9BA08C] placeholder:font-normal ${isH3 ? 'text-base' : 'text-xl'}`}
                    style={{ fontWeight: b.weight === 'light' ? 200 : b.weight === 'bold' ? 800 : 600 }} />
                </BlockShell>
              );
            }
            // ── Section break — the structural unit of a long document ─────
            // Everything after it belongs to a new section, which the supplier
            // reads and ticks off one at a time. A document with no breaks is
            // one section and behaves exactly as it always has.
            if (b.type === 'section_break') {
              // Numbered from the derived sections rather than counted here,
              // so the label always matches what the supplier's contents list
              // will show (a doc opening with a break has no phantom intro).
              const secIndex = editorSections.findIndex(s => s.id === b.id);
              return (
                <BlockShell key={b.id} label={secIndex >= 0 ? `Section ${secIndex + 1} starts here` : 'Section break'} {...shell}>
                  <div className="flex items-center gap-3">
                    <span className="h-px flex-1 bg-[#92D400]" aria-hidden="true" />
                    <input
                      value={b.title}
                      onChange={e => updateBlock(b.id, { title: e.target.value })}
                      onFocus={() => setFocusedBlockId(b.id)}
                      placeholder="Section title — shown in the supplier's contents list"
                      className={`${FONT} flex-[2] min-w-0 bg-transparent border-0 outline-none text-center text-base font-bold text-fusion-forest placeholder:text-[#9BA08C] placeholder:font-normal`} />
                    <span className="h-px flex-1 bg-[#92D400]" aria-hidden="true" />
                  </div>
                  <p className={`${FONT} text-[11px] text-[#9BA08C] text-center mt-1.5`}>
                    Starts a new section. The supplier must open every section before they can sign, and this becomes a page break in the PDF.
                  </p>
                </BlockShell>
              );
            }
            if (b.type === 'divider') {
              return (
                <BlockShell key={b.id} label="Divider" {...shell}>
                  <hr className="border-0 border-t border-[#DADCCD] my-2" />
                </BlockShell>
              );
            }
            if (b.type === 'table') {
              const setCell = (r, c, v) => updateBlock(b.id, { rows: b.rows.map((row, ri) => ri === r ? row.map((cell, ci) => ci === c ? v : cell) : row) });
              const addRow = () => updateBlock(b.id, { rows: [...b.rows, b.rows[0].map(() => '')] });
              const addCol = () => updateBlock(b.id, { rows: b.rows.map(r => [...r, '']) });
              const removeRow = (r) => updateBlock(b.id, { rows: b.rows.length > 1 ? b.rows.filter((_, ri) => ri !== r) : b.rows });
              const removeCol = (c) => updateBlock(b.id, { rows: b.rows[0].length > 1 ? b.rows.map(r => r.filter((_, ci) => ci !== c)) : b.rows });
              return (
                <BlockShell key={b.id} label="Table" {...shell}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <label className={`${FONT} flex items-center gap-1.5 text-[11px] text-[#51534A] cursor-pointer`}>
                      <input type="checkbox" checked={b.header !== false} onChange={e => updateBlock(b.id, { header: e.target.checked })} className="w-3.5 h-3.5 accent-active-green" />
                      First row is a header
                    </label>
                    <span className="w-px h-4 bg-[#DADCCD]" aria-hidden="true" />
                    <button type="button" onClick={addRow} className={`${FONT} text-[11px] font-semibold text-fusion-forest hover:underline`}>+ Row</button>
                    <button type="button" onClick={addCol} className={`${FONT} text-[11px] font-semibold text-fusion-forest hover:underline`}>+ Column</button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="border-collapse">
                      <tbody>
                        {b.rows.map((row, r) => (
                          <tr key={r}>
                            {row.map((cell, c) => (
                              <td key={c} className="border border-[#DADCCD] p-0">
                                <input
                                  value={cell}
                                  onChange={e => setCell(r, c, e.target.value)}
                                  onFocus={() => setFocusedBlockId(b.id)}
                                  placeholder={r === 0 && b.header !== false ? 'Column heading' : ''}
                                  className={`${FONT} w-full min-w-[120px] bg-transparent border-0 outline-none px-2.5 py-1.5 text-[13px] text-ink ${r === 0 && b.header !== false ? 'font-semibold' : ''}`} />
                              </td>
                            ))}
                            <td className="pl-1">
                              <button type="button" onClick={() => removeRow(r)} title="Remove this row"
                                className={`${FONT} text-[11px] text-[#9BA08C] hover:text-[#E0290A] px-1`}>×</button>
                            </td>
                          </tr>
                        ))}
                        <tr>
                          {b.rows[0].map((_, c) => (
                            <td key={c} className="text-center">
                              <button type="button" onClick={() => removeCol(c)} title="Remove this column"
                                className={`${FONT} text-[11px] text-[#9BA08C] hover:text-[#E0290A] px-1`}>×</button>
                            </td>
                          ))}
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </BlockShell>
              );
            }
            if (b.type === 'paragraph') {
              const fmt = (cmd) => paraRefs.current[b.id]?.exec(cmd);
              const toggleBullet = () => paraRefs.current[b.id]?.toggleBullet();
              return (
                <BlockShell key={b.id} label="Paragraph" {...shell}>
                  <div className="flex items-center gap-1 mb-1.5">
                    {[['bold', 'B', 'font-extrabold'], ['italic', 'I', 'italic'], ['underline', 'U', 'underline']].map(([cmd, glyph, cls]) => (
                      <button
                        key={cmd}
                        type="button"
                        // preventDefault on mousedown keeps the text selection in
                        // the editor while the button takes the click.
                        onMouseDown={(e) => { e.preventDefault(); fmt(cmd); }}
                        title={`${cmd[0].toUpperCase()}${cmd.slice(1)} (select text first)`}
                        className={`${FONT} ${cls} w-6 h-6 rounded flex items-center justify-center text-[13px] text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink`}>
                        {glyph}
                      </button>
                    ))}
                    {/* Size STEPS, not a point size. The author picks small /
                        normal / large; the document stylesheet decides what
                        those mean, so restyling every document stays one edit
                        and no document can drift off the house type scale. */}
                    <span className="w-px h-4 bg-[#DADCCD] mx-1" aria-hidden="true" />
                    {TEXT_SIZES.map(s => (
                      <button
                        key={s.key}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); paraRefs.current[b.id]?.setSize(s.key); }}
                        title={`${s.label} text (select text first)`}
                        className={`${FONT} px-1.5 h-6 rounded text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink ${s.key === 'sm' ? 'text-[10px]' : s.key === 'lg' ? 'text-[15px]' : 'text-[12px]'}`}>
                        A
                      </button>
                    ))}
                    <span className="w-px h-4 bg-[#DADCCD] mx-1" aria-hidden="true" />
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); toggleBullet(); }}
                      title="Bullet list (toggles the current line)"
                      className={`${FONT} w-6 h-6 rounded flex items-center justify-center text-[15px] leading-none text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink`}>
                      •
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); paraRefs.current[b.id]?.toggleNumbered(); }}
                      title="Numbered list (toggles the current line)"
                      className={`${FONT} w-6 h-6 rounded flex items-center justify-center text-[11px] font-bold leading-none text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink`}>
                      1.
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); paraRefs.current[b.id]?.indent(-1); }}
                      title="Outdent"
                      className={`${FONT} w-6 h-6 rounded flex items-center justify-center text-[13px] leading-none text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink`}>
                      ⇤
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); paraRefs.current[b.id]?.indent(+1); }}
                      title="Indent"
                      className={`${FONT} w-6 h-6 rounded flex items-center justify-center text-[13px] leading-none text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink`}>
                      ⇥
                    </button>
                    <span className="w-px h-4 bg-[#DADCCD] mx-1" aria-hidden="true" />
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        // eslint-disable-next-line no-alert
                        const href = window.prompt('Link address (https://… or mailto:…)');
                        if (href == null) return;
                        const ok = paraRefs.current[b.id]?.insertLink(href);
                        if (!ok) setError('That link wasn\'t added — select some text first, and use an https:// or mailto: address.');
                      }}
                      title="Link the selected text"
                      className={`${FONT} w-6 h-6 rounded flex items-center justify-center text-[12px] leading-none text-[#51534A] hover:bg-[#EEF1E6] hover:text-ink`}>
                      🔗
                    </button>
                  </div>
                  <ParagraphEditor
                    ref={(h) => { paraRefs.current[b.id] = h; }}
                    lines={b.lines}
                    placeholder="Body text. Enter for a new line, • for bullets, bold/italic from the toolbar, identity fields from the palette →"
                    onFocus={() => setFocusedBlockId(b.id)}
                    onChange={(lines) => updateBlock(b.id, { lines })} />
                </BlockShell>
              );
            }
            if (b.type === 'bullet') {
              const setItem = (i, v) => updateBlock(b.id, { items: b.items.map((it, j) => j === i ? v : it) });
              const addItem = () => updateBlock(b.id, { items: [...b.items, ''] });
              const removeItem = (i) => updateBlock(b.id, { items: b.items.length > 1 ? b.items.filter((_, j) => j !== i) : b.items });
              return (
                <BlockShell key={b.id} label="Bullet list" {...shell}>
                  <div className="flex flex-col gap-1.5">
                    {b.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-[#9BA08C] flex-shrink-0">•</span>
                        <input
                          value={it}
                          onChange={e => setItem(i, e.target.value)}
                          onFocus={() => setFocusedBlockId(b.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                          placeholder="List item"
                          className={`${FONT} flex-1 bg-transparent border-0 border-b border-line outline-none text-[15px] text-ink py-1 focus:border-[#92D400]`} />
                        <button type="button" onClick={() => removeItem(i)} disabled={b.items.length <= 1} aria-label="Remove item"
                          className="w-6 h-6 rounded flex items-center justify-center text-[#51534A] hover:text-[#E0290A] hover:bg-[#E0290A]/10 disabled:opacity-30 disabled:cursor-not-allowed">✕</button>
                      </div>
                    ))}
                    <button type="button" onClick={addItem} className={`${FONT} self-start text-[12px] font-semibold text-fusion-forest hover:underline mt-0.5`}>+ Add item</button>
                  </div>
                </BlockShell>
              );
            }
            if (b.type === 'clause') {
              const wc = wordCount(b.summary);
              const over = wc > CLAUSE_SUMMARY_MAX_WORDS;
              return (
                <BlockShell key={b.id} label="Clause · supplier must tick" {...shell}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-4 h-4 mt-1 rounded border-2 border-[#2E8FA1] flex-shrink-0" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <div className={`${FONT} text-[9px] font-bold uppercase tracking-[0.14em] text-[#9BA08C] mb-0.5`}>Summary the supplier ticks · ≤{CLAUSE_SUMMARY_MAX_WORDS} words</div>
                      <input
                        value={b.summary}
                        onChange={e => updateBlock(b.id, { summary: e.target.value })}
                        onFocus={() => setFocusedBlockId(b.id)}
                        placeholder="Short summary the supplier ticks (≤30 words)"
                        className={`${FONT} w-full bg-transparent border-0 outline-none text-[15px] font-semibold text-ink placeholder:text-[#9BA08C] placeholder:font-normal`} />
                      <div className={`text-[11px] mt-0.5 ${over ? 'text-[#E0290A] font-bold' : 'text-[#51534A]'}`}>Summary · {wc}/{CLAUSE_SUMMARY_MAX_WORDS} words</div>
                      <div className={`${FONT} text-[9px] font-bold uppercase tracking-[0.14em] text-[#9BA08C] mt-2 mb-0.5`}>Full wording · shown in full, no limit</div>
                      <textarea
                        value={b.full}
                        onChange={e => updateBlock(b.id, { full: e.target.value })}
                        onFocus={() => setFocusedBlockId(b.id)}
                        placeholder="Full wording of the clause"
                        rows={3}
                        className={`${inputCls} resize-y`} />
                    </div>
                  </div>
                </BlockShell>
              );
            }
            if (b.type === 'signature') {
              return (
                <BlockShell key={b.id} label="Signature · once, at the end" {...shell}>
                  <textarea
                    value={b.statement}
                    onChange={e => updateBlock(b.id, { statement: e.target.value })}
                    onFocus={() => setFocusedBlockId(b.id)}
                    placeholder="Authority statement the supplier signs under"
                    rows={2}
                    className={`${inputCls} resize-y`} />
                  <div className="mt-3 pt-3 border-t border-dashed border-line flex items-end gap-3">
                    <div className="flex-1">
                      <div className="h-8 border-b-2 border-ink/40" />
                      <span className="text-[10px] text-[#9BA08C] uppercase tracking-[0.15em]">Signature</span>
                    </div>
                    <div className="w-40">
                      <div className="h-8 border-b-2 border-ink/40" />
                      <span className="text-[10px] text-[#9BA08C] uppercase tracking-[0.15em]">Date</span>
                    </div>
                  </div>
                  {/* Extra signature rows — Name & job title, Company, Address…
                      The signature block is SUBSTANCE: everything here is typed by
                      the person signing, never prefilled (review brief §7). */}
                  <div className="mt-3 pt-3 border-t border-dashed border-line">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#9BA08C] mb-1">Extra fields in the signature block</div>
                    <p className="text-[11px] text-[#51534A] leading-snug mb-2">The person signing types these — the signature block is never prefilled.</p>
                    {(b.extras || []).map(ex => (
                      <div key={ex.id} className="flex items-center gap-2 mb-1.5">
                        <input
                          value={ex.label}
                          onChange={e => updateBlock(b.id, { extras: (b.extras || []).map(x => x.id === ex.id ? { ...x, label: e.target.value } : x) })}
                          placeholder="Field label (e.g. Name & job title)"
                          className={`${inputCls} flex-1`} />
                        <button type="button" title="Remove field"
                          onClick={() => updateBlock(b.id, { extras: (b.extras || []).filter(x => x.id !== ex.id) })}
                          className="w-7 h-7 rounded flex items-center justify-center text-[#51534A] hover:text-[#E0290A] hover:bg-[#E0290A]/10 flex-shrink-0">✕</button>
                      </div>
                    ))}
                    <button type="button"
                      onClick={() => updateBlock(b.id, { extras: [...(b.extras || []), { id: newBlockId(), label: '', source: null }] })}
                      className={`${FONT} mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest hover:opacity-80 bg-transparent border-0 cursor-pointer`}>
                      + Add field
                    </button>
                  </div>
                </BlockShell>
              );
            }
            return null;
          })}
        </div>

        {/* Palette + problems rail */}
        <div className="lg:sticky lg:top-[76px] self-start flex flex-col gap-5">
          {/* Add blocks */}
          <div className="bg-white border border-line rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C] mb-2.5">Add block</div>
            <div className="grid grid-cols-2 gap-2">
              <PaletteBtn onClick={() => addBlock('heading')}>Heading</PaletteBtn>
              <PaletteBtn onClick={() => addBlock('paragraph')}>Paragraph</PaletteBtn>
              <PaletteBtn onClick={() => addBlock('bullet')}>Bullet list</PaletteBtn>
              <PaletteBtn onClick={() => addBlock('clause')}>Clause</PaletteBtn>
              <PaletteBtn onClick={() => addBlock('table')}>Table</PaletteBtn>
              <PaletteBtn onClick={() => addBlock('divider')}>Divider</PaletteBtn>
              <PaletteBtn onClick={() => addBlock('signature')} disabled={hasSignature} title={hasSignature ? 'Only one signature block' : undefined}>Signature</PaletteBtn>
            </div>
          </div>

          {/* ── Sections ──────────────────────────────────────────────────
              Long documents are read a section at a time, so the author needs
              to see the shape they're creating. This is the same derivation
              the supplier's contents list uses, so what's listed here is
              exactly what they'll get. */}
          <div className="bg-white border border-line rounded-xl p-4 mb-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C] mb-2.5">Sections</div>
            <PaletteBtn onClick={() => addBlock('section_break')}>+ Section break</PaletteBtn>
            {editorSections.length > 1 ? (
              <ol className={`${FONT} mt-3 space-y-1`}>
                {editorSections.map((s, si) => (
                  <li key={s.id} className="flex items-baseline gap-2 text-[12px] text-[#51534A]">
                    <span className="text-[#9BA08C] tabular-nums flex-shrink-0">{si + 1}.</span>
                    <span className="min-w-0 truncate">{s.title}</span>
                    {s.hasSignature && <span className="text-[10px] text-fusion-forest font-semibold flex-shrink-0">signs</span>}
                  </li>
                ))}
              </ol>
            ) : (
              <p className={`${FONT} mt-2.5 text-[11px] text-[#9BA08C] leading-relaxed`}>
                One section. Add a section break to split a long document — the
                supplier then reads it a section at a time and must open every
                one before they can sign.
              </p>
            )}
          </div>

          {/* Identity fields */}
          <div className="bg-white border border-line rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C] mb-1.5">Insert identity field</div>
            <p className="text-[11px] text-[#51534A] leading-snug mb-2.5">Prefilled from the supplier's account and editable by them. Identity only — never the substance they're attesting to.</p>
            <div className="flex flex-wrap gap-1.5">
              {FIELD_SOURCES.map(f => (
                // preventDefault on mousedown keeps the caret in the paragraph
                // so the chip drops in at the cursor, not appended at the end.
                <button key={f.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertFieldIntoFocused(f.key)}
                  title={`e.g. ${f.example}`}
                  className={`${FONT} text-[11px] font-semibold rounded-full px-2.5 py-1 bg-[#2E8FA1]/10 text-[#2E8FA1] border border-[#2E8FA1]/30 hover:bg-[#2E8FA1]/20 transition-colors`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* They fill in — supplier-completed inputs (never prefilled). First
              name / surname are separate on purpose. Inserted at the caret like
              identity fields; distinct green chips so the two groups don't blur. */}
          <div className="bg-white border border-line rounded-xl p-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C] mb-1.5">Insert a field they fill in</div>
            <p className="text-[11px] text-[#51534A] leading-snug mb-2.5">The supplier completes these — never prefilled. First name and surname are separate fields.</p>
            <div className="flex flex-wrap gap-1.5">
              {FILL_FIELDS.map(f => (
                <button key={f.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertInputIntoFocused(f.key)}
                  className={`${FONT} text-[11px] font-semibold rounded-full px-2.5 py-1 bg-[#2F6E14]/10 text-[#2F6E14] border border-[#2F6E14]/30 hover:bg-[#2F6E14]/20 transition-colors`}>
                  {f.label}
                </button>
              ))}
            </div>
            {/* Add a custom, named fill-in field — drops in at the paragraph's
                caret (remembered while the composer input has focus). */}
            <div className="mt-3 pt-3 border-t border-line">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9BA08C] mb-1.5">Add a custom field</div>
              <div className="flex items-center gap-1.5">
                <input
                  value={customFieldLabel}
                  onChange={e => setCustomFieldLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); insertCustomFieldIntoFocused(); } }}
                  placeholder="Field name — e.g. Site address"
                  className={`${FONT} flex-1 min-w-0 bg-white border border-line rounded-lg px-2.5 py-1.5 text-[12px] text-ink focus:outline-none focus:border-[#2F6E14]`} />
                <select value={customFieldType} onChange={e => setCustomFieldType(e.target.value)}
                  className={`${FONT} bg-white border border-line rounded-lg px-2 py-1.5 text-[12px] text-fusion-mid-grey focus:outline-none focus:border-[#2F6E14]`}>
                  <option value="short_text">Short text</option>
                  <option value="long_text">Long text</option>
                  <option value="number">Number</option>
                  <option value="fill_date">Date</option>
                  <option value="yes_no">Yes / no</option>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                </select>
                <button type="button" onClick={insertCustomFieldIntoFocused}
                  className={`${FONT} text-[11px] font-bold rounded-lg px-2.5 py-1.5 bg-[#2F6E14] text-white hover:brightness-110 transition-[filter]`}>
                  Add
                </button>
              </div>
              <p className="text-[10px] text-[#51534A] mt-1.5">Click into the paragraph where you want it, then add.</p>
            </div>
          </div>

          {/* Problems rail */}
          <div className={`rounded-xl p-4 border ${problems.length > 0 ? 'bg-[#E0290A]/[0.04] border-[#E0290A]/30' : 'bg-[#92D400]/[0.05] border-[#92D400]/30'}`} aria-live="polite">
            <div className={`text-[10px] font-bold uppercase tracking-[0.18em] mb-2 ${problems.length > 0 ? 'text-[#9c2b12]' : 'text-fusion-forest'}`}>
              {problems.length > 0 ? `${problems.length} problem${problems.length === 1 ? '' : 's'} to fix` : 'Ready to publish'}
            </div>
            {problems.length === 0 ? (
              <p className="text-[12px] text-[#51534A] leading-snug">Everything checks out — publishing will make this the live version suppliers read and sign.</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {problems.map((p, i) => (
                  <li key={i} className="text-[12px] text-ink leading-snug flex gap-1.5">
                    <span className="text-[#E0290A] flex-shrink-0">•</span>{p.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      {/* Preview modes — both render the draft through the REAL document
          renderer. 'supplier' fills sample answers (§6); 'document' shows the
          blank branded document (cover + content) for reading. Nothing is
          submitted. */}
      {previewOpen && (() => {
        const isDoc = previewMode === 'document';
        return (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={isDoc ? 'View document' : 'Preview as supplier'}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3 sm:p-6"
            onClick={() => setPreviewOpen(false)}>
            <div className="bg-white rounded-2xl shadow-lift w-full max-w-[1200px] h-[94vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-line">
                <div className="min-w-0">
                  <div className={`${FONT} text-[10px] font-bold uppercase tracking-[0.16em] text-[#2E8FA1]`}>{isDoc ? 'View document' : 'Preview as supplier'}</div>
                  <div className={`${FONT} text-sm font-bold text-ink truncate`}>{doc?.document_name || 'Document'}</div>
                </div>
                <button type="button" onClick={() => setPreviewOpen(false)} aria-label="Close preview"
                  className={`${FONT} w-8 h-8 rounded-lg flex items-center justify-center text-[#51534A] hover:bg-[#EEF1E6]`}>✕</button>
              </div>
              <div className="p-3 bg-[#f3f5ee] flex-1 min-h-0">
                <iframe title={isDoc ? 'View document' : 'Preview as supplier'} srcDoc={isDoc ? documentHtml : previewHtml} sandbox=""
                  className="w-full h-full rounded-lg border border-line bg-white" />
              </div>
              <div className={`${FONT} px-5 py-2.5 border-t border-line text-[11px] text-[#51534A]`}>
                {isDoc
                  ? 'The document as it reads — cover, branding and content. Fill-in fields show blank.'
                  : 'Preview with sample details — a supplier fills these in for real, and nothing here is submitted.'}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function PaletteBtn({ onClick, disabled, title, children }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={`${FONT} text-xs font-semibold rounded-lg px-3 py-2 border transition-colors ${disabled ? 'border-line text-[#c4c8b8] cursor-not-allowed' : 'border-line text-fusion-mid-grey hover:border-[#92D400] hover:text-fusion-forest'}`}>
      {children}
    </button>
  );
}
